/**
 * Dictation-off guidance — the copy and the Settings deep-link for the one
 * failure the user can actually fix.
 *
 * Both desktop platforms have a switch outside the app that leaves dictation
 * looking available and then failing at runtime, and in both cases the remedy
 * is a Settings page rather than anything Denker can do:
 *
 *   - **macOS** gates `SFSpeechRecognizer` behind the master Dictation toggle
 *     (System Settings → Keyboard → Dictation). Apple's error text says "Siri
 *     and Dictation are disabled", which conflates Siri — enabling Dictation
 *     alone fixes it — so we NEVER surface Apple's raw string.
 *   - **Windows** gates `SpeechRecognizer` behind the speech privacy policy
 *     (Settings → Privacy & security → Speech). `StartAsync` fails with
 *     `0x80045509` until it is accepted, which `windows_stt.rs` reports as
 *     `speech_privacy_denied`.
 *
 * The feedback surface is an actionable card rendered by voice-indicator.tsx
 * at a FIXED screen position (bottom-center), NOT following the cursor — a
 * cursor-following button moves away as the user reaches for it. The cursor
 * overlay component is the ONLY surface that renders in compact mode, where
 * dictation actually runs — the in-app toast manager is not mounted there, and
 * a native notification needs Notification Center authorization the app may not
 * hold. The card shows the hint plus an "Open Settings" button.
 */
import { translate } from "@/i18n";
import { desktopDictationMessages } from "@/i18n/messages/desktop-dictation";
import { isWindowsPlatform } from "@/lib/os-platform";
import { openDictationSettings as openMacDictationSettings } from "./onboarding/permissions-deep-links";
import { openWindowsPrivacyPane } from "./onboarding/windows-privacy-deep-links";

/**
 * Short label shown in the cursor card (voice-indicator.tsx).
 *
 * A function rather than a constant because the two platforms disable
 * different things, and naming the wrong one sends the user to a page where
 * nothing looks wrong. Windows users have Dictation (Win+H) working fine while
 * this fails; telling them "Dictation is off" would be flatly untrue.
 */
export function dictationOffHint(): string {
  return translate(
    desktopDictationMessages,
    isWindowsPlatform() ? "speechRecognitionOff" : "dictationOff",
  );
}

/**
 * Which failures the user can fix from a Settings page.
 *
 * `speech_privacy_denied` is the Windows member of this family: a switch
 * outside the app, off by default, that leaves dictation looking available and
 * then failing. Leaving it out would give Windows users the generic error
 * instead of the card with the button that fixes it.
 */
export function classifyDictationError(
  code: string | undefined,
): "dictation_off" | "generic" {
  return code === "dictation_disabled" ||
    code === "no_on_device" ||
    code === "speech_privacy_denied"
    ? "dictation_off"
    : "generic";
}

/** What went wrong, for the card that shows it (voice-indicator.tsx). */
export type DictationErrorKind =
  | "dictation_off"
  | "whisper_preparing"
  | "generic";

/**
 * The dictation state after a refused start. An Intel Mac dictates with
 * Whisper alone and refuses a start until Whisper is ready
 * (`whisper_preparing`, the `code: message` form a rejected command throws);
 * anything else is generic.
 */
export function dictationStartFailure(error: unknown): {
  status: "error";
  lastError: string;
  errorKind: DictationErrorKind;
} {
  const message = String(error);
  return {
    status: "error",
    lastError: message,
    errorKind: message.startsWith("whisper_preparing:")
      ? "whisper_preparing"
      : "generic",
  };
}

const PERMISSION_DENIED = "permission_denied";

/**
 * A voice start refused for the microphone or speech-recognition grant.
 * Decided by the native `permission_denied` code only — an event's `code`, or
 * the `code: message` form a rejected command throws. Message words decide
 * nothing: a device failure names its microphone ("MacBook Pro Microphone")
 * and must stay a start failure, not a permission prompt.
 */
export function isVoicePermissionError(
  code: string | undefined,
  message?: string,
): boolean {
  return (
    code === PERMISSION_DENIED || !!message?.startsWith(`${PERMISSION_DENIED}:`)
  );
}

/** Open the settings page that owns the switch this platform is blocked on. */
export async function openDictationSettings(): Promise<void> {
  if (isWindowsPlatform()) {
    await openWindowsPrivacyPane("speech");
    return;
  }
  await openMacDictationSettings();
}
